import { useEffect, useRef, type ReactNode } from "react"
import { brand } from "@/assets/brand"
import { MessageBubble } from "./MessageBubble"
import type { ChatMessage } from "./MessageBubble"
import { Icon } from "@/components/Icon"

interface MessageListProps {
  messages: ChatMessage[]
  isLoading?: boolean
  /** Customises the empty-state copy (e.g. a post-draft "ask me anything"). */
  empty?: { title: string; body: string }
  /**
   * Locally-rendered node shown at the top of the stream, before any runtime
   * messages (e.g. the OIO completion summary card). It replaces the empty
   * state when there are no messages — the lead IS the content then — and it
   * never enters the runtime's message history.
   */
  lead?: ReactNode
}

export function MessageList({ messages, isLoading, empty, lead }: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages.length, isLoading])

  if (messages.length === 0 && !isLoading && !lead) {
    return (
      <div className="chat__stream">
        <div className="empty" style={{ height: "auto", padding: "40px 20px" }}>
          <div className="empty__ico">
            <Icon name="message" size={22} />
          </div>
          <h3>{empty?.title ?? "Ready to draft"}</h3>
          <p>
            {empty?.body ??
              "Upload the workspaces and generate the order — the assistant will reason here as it works, and answer your questions once the draft is ready."}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="chat__stream">
      {lead}
      {messages.map((msg) => (
        <MessageBubble key={msg.id} message={msg} />
      ))}

      {isLoading && (
        <div className="msg">
          <div className="msg__av msg__av--ai">
            <img className="msg__av-logo msg__av-logo--thinking" src={brand.emblem} alt="" />
          </div>
          <div style={{ minWidth: 0, display: "flex", alignItems: "center" }}>
            <span className="spinner" />
          </div>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  )
}
